<template>
  <standard-dialog
    v-model="model"
    :title="$t('new_contract') + ' — ' + (restaurant.name || '')"
    icon="mdi-file-document-edit-outline"
    :max-width="760"
    :hide-actions="loading"
  >
    <global-confirm-box ref="confirmBox" />

    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else>
      <v-row>
        <v-col cols="12" sm="6">
          <text-field
            v-model="form.contract_number"
            :label="$t('contract_number')"
            required
            :rules="[rules.required]"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <date-picker
            v-model="form.contract_date"
            single-date
            :label="$t('contract_date')"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <date-picker
            v-model="form.valid_from"
            single-date
            :label="$t('contract_valid_from')"
            :hint="$t('contract_valid_from_hint')"
            persistent-hint
          />
        </v-col>
        <v-col cols="12" sm="6">
          <date-picker
            v-model="form.valid_to"
            single-date
            :label="$t('contract_valid_to')"
            :min="minValidTo"
            :disabled="form.no_end_date"
          />
          <v-checkbox
            v-model="form.no_end_date"
            :label="$t('contract_no_end_date')"
            hide-details
            dense
            class="mt-0"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <dropdown
            v-model="form.payout_schedule"
            :items="payoutScheduleOptions"
            item-text="text"
            item-value="value"
            :label="$t('payout_schedule')"
            required
          />
        </v-col>
        <v-col cols="12" sm="6" v-if="form.payout_schedule === 'THRESHOLD'">
          <text-field
            v-model="form.payout_threshold_amount"
            type="number"
            :label="$t('payout_threshold_amount')"
            required
          />
          <span class="caption error--text d-block" v-if="errors.payout_threshold_amount">
            {{ errors.payout_threshold_amount }}
          </span>
        </v-col>
        <v-col cols="12">
          <text-field
            v-model="form.document_url"
            :label="$t('contract_document_link')"
            :hint="$t('contract_document_link_hint')"
            persistent-hint
          />
        </v-col>
        <v-col cols="12">
          <text-area v-model="form.note" :label="$t('note')" rows="2" />
        </v-col>
      </v-row>

      <v-alert v-if="closingNotice" type="info" text dense class="mb-4">
        {{ closingNotice }}
      </v-alert>

      <span class="subtitle-2 font-weight-medium d-block mb-2">{{
        $t("contract_items_label")
      }}</span>

      <v-card
        v-for="item in items"
        :key="item.service_id"
        outlined
        class="mb-2 pa-3 contract-item-card"
        :class="{ 'contract-item-card--selected': item.selected }"
      >
        <v-row no-gutters align="center">
          <v-checkbox
            v-model="item.selected"
            hide-details
            class="shrink mt-0 pt-0 mr-2"
          />
          <span class="font-weight-medium">{{ item.name }}</span>
        </v-row>

        <template v-if="item.selected">
          <v-row no-gutters class="mt-2" align="center">
            <v-col cols="12" sm="5" class="pr-sm-2">
              <dropdown
                v-model="item.paying_type"
                :items="payingTypeOptions"
                item-text="text"
                item-value="value"
                :label="$t('payment_method')"
              />
            </v-col>
            <v-col cols="12" sm="4" class="pr-sm-2">
              <text-field
                v-model="item.amount"
                type="number"
                :label="$t('amount') + ' ' + itemUnit(item)"
                required
              />
            </v-col>
          </v-row>
          <span class="caption error--text d-block" v-if="errors.items[item.service_id]">
            {{ errors.items[item.service_id] }}
          </span>

          <div v-if="item.uses_cash_flow_model" class="mt-2">
            <span class="caption font-weight-medium d-block mb-1">
              {{ $t("cash_flow_model_label") }} <span class="error--text">*</span>
            </span>
            <v-radio-group v-model="item.cash_flow_model" hide-details class="mt-0">
              <v-radio
                v-for="option in cashFlowModelOptions"
                :key="option.value"
                :value="option.value"
                :label="option.text"
              />
            </v-radio-group>
            <span
              class="caption error--text d-block"
              v-if="errors.cashFlow[item.service_id]"
            >
              {{ errors.cashFlow[item.service_id] }}
            </span>
          </div>
        </template>
      </v-card>

      <div class="caption grey--text mt-2">
        {{ $t("contract_items_count", { count: selectedCount }) }}
      </div>
    </template>

    <template v-slot:actions>
      <v-btn text class="mr-2" @click="close">{{ $t("cancel") }}</v-btn>
      <primary-button
        :loading="saving"
        :disabled="saving || !selectedCount"
        @click="save"
      >
        {{ $t("save") }}
      </primary-button>
    </template>
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import hasModel from "@/mixins/hasModel";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";
import restaurantService from "@/services/restaurant";
import contractsService from "@/services/contracts";

const SANITY_LIMITS = {
  2: 40,
  1: 5000,
  3: 100,
};

export default {
  name: "ContractFormDialog",
  mixins: [hasModel, hasValidation, hasSnackbar],
  components: {
    StandardDialog,
  },
  props: {
    restaurant: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      options: {},
      items: [],
      currentContract: null,
      form: this.emptyForm(),
      errors: { items: {}, cashFlow: {}, payout_threshold_amount: "" },
    };
  },
  watch: {
    model(val) {
      if (val) this.fetchData();
    },
  },
  computed: {
    payoutScheduleOptions() {
      return Object.entries(this.options.payout_schedules || {}).map(([value, text]) => ({
        value,
        text,
      }));
    },
    payingTypeOptions() {
      return Object.entries(this.options.paying_types || {}).map(([value, text]) => ({
        value: Number(value),
        text,
      }));
    },
    cashFlowModelOptions() {
      return Object.entries(this.options.cash_flow_models || {}).map(([value, text]) => ({
        value,
        text,
      }));
    },
    selectedCount() {
      return this.items.filter((i) => i.selected).length;
    },
    minValidTo() {
      return this.form.valid_from || this.form.contract_date || undefined;
    },
    closingNotice() {
      if (!this.currentContract) return "";
      const startDate = this.form.valid_from || this.form.contract_date;
      if (!startDate) return "";
      const closingDate = new Date(startDate + "T00:00:00");
      closingDate.setDate(closingDate.getDate() - 1);
      return this.$t("contract_will_close_previous", {
        number: this.currentContract.contract_number,
        date: this.localeDate(closingDate.toISOString().substr(0, 10)),
      });
    },
  },
  methods: {
    close() {
      this.model = false;
    },
    emptyForm() {
      return {
        contract_number: "",
        contract_date: new Date().toISOString().substr(0, 10),
        valid_from: "",
        valid_to: "",
        no_end_date: false,
        document_url: "",
        note: "",
        payout_schedule: null,
        payout_threshold_amount: "",
      };
    },
    itemUnit(item) {
      if (Number(item.paying_type) === 2) return "%";
      return this.getCurrencyByRestaurantId(this.restaurant.id) || "KM";
    },
    async fetchData() {
      this.loading = true;
      this.form = this.emptyForm();
      this.errors = { items: {}, cashFlow: {}, payout_threshold_amount: "" };
      try {
        const [servicesRes, contractsRes] = await Promise.all([
          restaurantService.getRestaurantServices(this.restaurant.id),
          contractsService.getRestaurantContracts(this.restaurant.id).catch(() => null),
        ]);

        this.options = servicesRes.data.options || {};
        this.items = (servicesRes.data.services || []).map((service) => ({
          service_id: service.service_id,
          name: service.name,
          uses_cash_flow_model: service.uses_cash_flow_model,
          selected: false,
          paying_type: this.payingTypeOptions[0]?.value ?? null,
          amount: "",
          cash_flow_model: null,
        }));

        this.currentContract = contractsRes?.data?.contracts?.[0] || null;
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_restaurant_services"));
        this.model = false;
      } finally {
        this.loading = false;
      }
    },
    validate() {
      const errors = { items: {}, cashFlow: {}, payout_threshold_amount: "" };
      let valid = true;

      if (!this.form.contract_number || !this.form.contract_date) valid = false;
      if (
        this.form.payout_schedule === "THRESHOLD" &&
        !this.form.payout_threshold_amount
      ) {
        errors.payout_threshold_amount = this.$t("validation.required");
        valid = false;
      }
      if (!this.form.payout_schedule) valid = false;

      const selected = this.items.filter((i) => i.selected);
      if (!selected.length) valid = false;

      selected.forEach((item) => {
        const amount = Number(item.amount);
        if (!item.paying_type || !item.amount || isNaN(amount) || amount <= 0) {
          errors.items[item.service_id] = this.$t("validation.required");
          valid = false;
        }
        if (item.uses_cash_flow_model && !item.cash_flow_model) {
          errors.cashFlow[item.service_id] = this.$t("cash_flow_model_required");
          valid = false;
        }
      });

      this.errors = errors;
      return valid;
    },
    async confirmSanityIfNeeded() {
      const selected = this.items.filter((i) => i.selected);
      const suspicious = selected.filter((item) => {
        const limit = SANITY_LIMITS[Number(item.paying_type)];
        return limit && Number(item.amount) > limit;
      });
      if (!suspicious.length) return true;
      try {
        await this.$refs.confirmBox.open(
          this.$t("please_confirm"),
          this.$t("amount_looks_unusual"),
          { color: "warning" }
        );
        return true;
      } catch (e) {
        return false;
      }
    },
    buildPayload() {
      return {
        contract_number: this.form.contract_number,
        contract_date: this.form.contract_date,
        valid_from: this.form.valid_from || null,
        valid_to: this.form.no_end_date ? null : this.form.valid_to || null,
        document_url: this.form.document_url || null,
        note: this.form.note || null,
        payout_schedule: this.form.payout_schedule,
        payout_threshold_amount:
          this.form.payout_threshold_amount !== "" &&
          this.form.payout_threshold_amount != null
            ? Number(this.form.payout_threshold_amount)
            : null,
        items: this.items
          .filter((i) => i.selected)
          .map((item) => {
            const payloadItem = {
              service_id: item.service_id,
              paying_type: item.paying_type,
              amount: Number(item.amount),
            };
            if (item.uses_cash_flow_model) {
              payloadItem.cash_flow_model = item.cash_flow_model;
            }
            return payloadItem;
          }),
      };
    },
    async save() {
      if (!this.validate()) {
        this.showErrorSnackbar(this.$t("please_fix_errors"));
        return;
      }
      const proceed = await this.confirmSanityIfNeeded();
      if (!proceed) return;

      this.saving = true;
      try {
        const { data } = await contractsService.createRestaurantContract(
          this.restaurant.id,
          this.buildPayload()
        );
        this.showSuccessSnackbar(data.message || this.$t("contract_saved_successfully"));
        this.$emit("saved", data.contract);
        this.model = false;
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        this.saving = false;
      }
    },
    handleSaveError(error) {
      if (error?.response?.status === 422) {
        this.showErrorSnackbar(
          error.response.data?.message || this.$t("please_fix_errors")
        );
      } else {
        this.showErrorSnackbar(this.$t("error_saving_contract"));
      }
    },
  },
};
</script>

<style scoped>
.contract-item-card {
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.contract-item-card--selected {
  border-color: var(--v-primary-base, #1976d2) !important;
  background-color: rgba(25, 118, 210, 0.04);
}
</style>
